import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

/** A client's materialized document folder with nested children. */
export interface ClientFolderNode {
  id: string;
  name: string;
  parentFolderId: string | null;
  clientId: string | null;
  isDefaultUploadTarget: boolean;
  children: ClientFolderNode[];
}

export interface ApplyTemplateResult {
  created: number;
  rootFolderId: string;
  templateId: string | null;
}

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const clientFolderKeys = {
  all: ['client-folders'] as const,
  tree: (clientId: string) => [...clientFolderKeys.all, 'tree', clientId] as const,
};

// ─── Hooks ──────────────────────────────────────────────────────────────────

/** Fetches a client's document folders assembled into a nested tree. */
export function useClientFolderTree(clientId: string | undefined) {
  return useQuery({
    queryKey: clientFolderKeys.tree(clientId ?? ''),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Record<string, unknown>[]>>(
        '/documents/folders/tree',
        { params: { client_id: clientId } },
      );
      return (response.data.data ?? []).map(mapFolder);
    },
    enabled: !!clientId,
  });
}

/**
 * Applies a folder template to a client, materializing its folders. When
 * templateId is omitted, the contact's assigned template (or tenant default)
 * is used.
 */
export function useApplyFolderTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { clientId: string; templateId?: string | null }) => {
      const response = await apiClient.post<ApiResponse<ApplyTemplateResult>>(
        '/documents/folders/apply-template',
        { client_id: params.clientId, template_id: params.templateId ?? null },
      );
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: clientFolderKeys.tree(variables.clientId) });
    },
  });
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function mapFolder(raw: Record<string, unknown>): ClientFolderNode {
  return {
    id: raw.id as string,
    name: (raw.name as string) ?? '',
    parentFolderId: (raw.parent_folder_id as string) ?? (raw.parentFolderId as string) ?? null,
    clientId: (raw.client_id as string) ?? (raw.clientId as string) ?? null,
    isDefaultUploadTarget:
      (raw.is_default_upload_target as boolean) ?? (raw.isDefaultUploadTarget as boolean) ?? false,
    children: Array.isArray(raw.children)
      ? (raw.children as Record<string, unknown>[]).map(mapFolder)
      : [],
  };
}

/**
 * Flattens a client folder tree into indented options for a select, and
 * surfaces the id of the default upload target (if any).
 */
export function flattenClientFolders(nodes: ClientFolderNode[]): {
  options: Array<{ value: string; label: string }>;
  defaultId: string | null;
} {
  const options: Array<{ value: string; label: string }> = [];
  let defaultId: string | null = null;

  const walk = (list: ClientFolderNode[], depth: number) => {
    for (const node of list) {
      const indent = '\u00A0\u00A0'.repeat(depth);
      const suffix = node.isDefaultUploadTarget ? ' (default)' : '';
      options.push({ value: node.id, label: `${indent}${node.name}${suffix}` });
      if (node.isDefaultUploadTarget) defaultId = node.id;
      if (node.children.length > 0) walk(node.children, depth + 1);
    }
  };

  walk(nodes, 0);
  return { options, defaultId };
}
